import { AxiosResponse } from 'axios'
import { SET_TICKET_COMMENTS, SET_TICKET_COMMENT, SET_TICKET_IN_THREAD, SET_ERRORMSG } from '../mutation_types'
import {
    CLEAR_TICKET_COMMENTS_ISLOADING,
    DELETE_TICKET_COMMENT,
    EDIT_TICKET_COMMENT,
    GET_TICKET_COMMENTS,
    ADD_TICKET_COMMENT,
    CLEAR_TICKET_COMMENTS,
} from '../action_types'
import { StateObject } from '@/dtos/state.dto'
import TicketComment from '@/services/ticket_comment'
import { Module } from 'vuex'
import { EditTicketComment, GetTicketComment, AddTicketComment } from '@/dtos/ticket_comment.dto'
import { isOlderCopy } from '@/utils/ticket'

// The one open conversation: its messages, and the ticket as the API last
// described it for the signed-in side (status, unread count, read marks).
interface ThreadState extends StateObject {
    ticket: any
}

const ticketComment: Module<any, {}> = {
    state: {
        ticketComment: {
            data: null,
            isLoading: false
        },
        ticketComments: {
            data: null,
            ticket: null,
            isLoading: false
        }
    },
    getters: {
        ticketComment: (state: { ticketComment: any }) => state.ticketComment,
        ticketComments: (state: { ticketComments: any }) => state.ticketComments
    },
    mutations: {
        [SET_TICKET_COMMENT]: (state: { ticketComment: StateObject }, payload: { data: null; status: any; isLoading: any }) => { payload.data != null ? state.ticketComment.data = payload.data : null, state.ticketComment.isLoading = payload.isLoading },
        [SET_TICKET_COMMENTS]: (state: { ticketComments: ThreadState }, payload: { data?: any; ticket?: any; isLoading: boolean }) => {
            if (payload.data !== undefined) state.ticketComments.data = payload.data
            // A poll that left before the ticket was changed here (renamed)
            // comes back with it as it was; the newer copy is kept.
            const current = state.ticketComments.ticket
            const stale = Boolean(payload.ticket && current && payload.ticket.id === current.id && isOlderCopy(payload.ticket, current))
            if (payload.ticket !== undefined && !stale) state.ticketComments.ticket = payload.ticket
            state.ticketComments.isLoading = payload.isLoading
        },
        // The ticket as an edit made here left it, over the open conversation's
        // copy — which the modal shows it from. Saved before the conversation
        // first came in, it stands in for that copy, so the first load (older)
        // is not let over it either.
        [SET_TICKET_IN_THREAD]: (state: { ticketComments: ThreadState }, payload: any) => {
            if (!payload?.id) return
            const current = state.ticketComments.ticket
            if (!current) state.ticketComments.ticket = payload
            else if (current.id === payload.id) state.ticketComments.ticket = { ...current, ...payload }
        }
    },
    actions: {
        [ADD_TICKET_COMMENT]: async({ commit }: any, payload: AddTicketComment) => {
            commit(SET_TICKET_COMMENT, { data: null, isLoading: true })

            // The API's own errors are toasted by its interceptor; a send that
            // never got an answer is not. A timeout already said so there.
            // Files too big for the servers in front of the API end the same
            // way: their refusal comes without the headers the browser needs
            // to read it.
            let result: AxiosResponse
            try {
                result = await TicketComment.registerTicketComment(payload)
            } catch (error: any) {
                commit(SET_TICKET_COMMENT, { isLoading: false })
                if (error?.code !== 'ECONNABORTED') {
                    commit(SET_ERRORMSG, payload.attachments?.length
                        ? "Your message couldn't be sent. Check your connection, or try fewer or smaller files."
                        : "Your message couldn't be sent. Check your connection and try again.")
                }
                return false;
            }

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET_COMMENT, { data: result.data.data, isLoading: false })
                    return true;
                }
                commit(SET_TICKET_COMMENT, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET_COMMENT, { isLoading: false })
                return false;
            }
        },
        // `silent` refreshes the open conversation behind what is shown (a
        // poll), instead of clearing it first.
        [GET_TICKET_COMMENTS]: async({ commit }: any, payload: GetTicketComment & { silent?: boolean }) => {
            const { silent, ...params } = payload
            if (!silent) commit(SET_TICKET_COMMENTS, { data: null, ticket: null, isLoading: true })

            let result: AxiosResponse = await TicketComment.getTicketComment(params)

            if(result) {
                if (result.data.status) {
                    const { ticket, comments } = result.data.data || {}
                    commit(SET_TICKET_COMMENTS, { data: Array.isArray(comments) ? comments : [], ticket: ticket || null, isLoading: false })
                    return true;
                }
                commit(SET_TICKET_COMMENTS, { isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET_COMMENTS, { isLoading: false })
                return false;
            }
        },
        [EDIT_TICKET_COMMENT]: async({ commit }: any, payload: EditTicketComment) => {
            commit(SET_TICKET_COMMENT, { data: null, isLoading: true })

            let result: AxiosResponse = await TicketComment.editTicketComment(payload)

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET_COMMENT, { data: result.data.data, isLoading: false })
                    return true;
                }
                commit(SET_TICKET_COMMENT, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET_COMMENT, { isLoading: false })
                return false;
            }
        },
        [DELETE_TICKET_COMMENT]: async({ commit }: any, payload: string) => {
            commit(SET_TICKET_COMMENT, { data: null, isLoading: true })

            let result: AxiosResponse = await TicketComment.deleteTicketComment(payload)

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET_COMMENT, { data: result.data.data, isLoading: false })
                    return true;
                }
                commit(SET_TICKET_COMMENT, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET_COMMENT, { isLoading: false })
                return false;
            }
        },
        [CLEAR_TICKET_COMMENTS]: ({ commit }) => {
            commit(SET_TICKET_COMMENT, { data: null, isLoading: false })
            commit(SET_TICKET_COMMENTS, { data: [], ticket: null, isLoading: false })
        },
        [CLEAR_TICKET_COMMENTS_ISLOADING]: ({ commit }) => {
            commit(SET_TICKET_COMMENT, { isLoading: false })
            commit(SET_TICKET_COMMENTS, { isLoading: false })
        }
    }
}

export default ticketComment
